'use client';

import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { getAuthServerUrl } from '@wt/utilities/database';
import { getSiteMode } from '@wt/utilities/siteMode';
import { loadPassportOfferScript } from '@wt/utilities/webcomponents';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';

const getDefaultReferer = () => {
  if (getSiteMode() === 'wheretaken') return 'wheretaken-2.0';
  if (getSiteMode() === 'whentaken-movies') return 'whentaken-movies';
  return 'whentaken';
};

// Hosts the externally-served <teuteuf-passport-offer> web component. The
// element is created imperatively (rather than written directly in JSX) so
// nothing tries to server-render an undefined custom element — same
// approach the login page used before it was packaged as
// @teuteuf-games/login-page.
export const PassportContent = () => {
  const router = useRouter();
  const hostRef = useRef<HTMLDivElement>(null);
  const { resolvedTheme } = useTheme();

  // window isn't available during SSR/prerender, so default by site mode
  // and correct to the localhost referer after mount.
  const [referer, setReferer] = useState(getDefaultReferer);
  useEffect(() => {
    if (window.location.href.includes('localhost')) {
      setReferer('localhost-3001');
    }
  }, []);

  useEffect(() => {
    loadPassportOfferScript();

    const host = hostRef.current;
    if (!host) return;

    const redirectUrl = new URL('/paymentreturn', window.location.origin).href;

    const element = document.createElement('teuteuf-passport-offer');
    element.setAttribute('lang', 'en');
    element.setAttribute('referer', referer);
    element.setAttribute('redirectUrl', redirectUrl);
    element.setAttribute('loginHref', '/passport');
    element.setAttribute('authServerUrl', getAuthServerUrl());
    element.setAttribute('theme', resolvedTheme === 'light' ? 'light' : 'dark');
    host.replaceChildren(element);

    return () => {
      host.replaceChildren();
    };
  }, [referer, resolvedTheme]);

  return (
    <div className="flex h-full flex-1 grow flex-col items-center px-4 md:px-8">
      <div className="md:bg-base-200 my-6 flex w-full max-w-4xl flex-col space-y-6 rounded-3xl p-4 text-lg md:p-6">
        <div className="flex w-full flex-row items-center justify-between">
          <div onClick={() => router.back()}>
            <ChevronLeftRoundedIcon className="m-0 fill-current text-2xl leading-none hover:cursor-pointer" />
          </div>
          <div className="mx-20 grow text-center">
            <span className="text-xl font-medium lg:text-4xl">Remove Ads</span>
          </div>
          <Link href={'/'} className="hidden md:flex">
            <CloseRoundedIcon className="m-0 fill-current text-2xl leading-none" />
          </Link>
        </div>
        <div ref={hostRef} className="w-full" />
      </div>
    </div>
  );
};
